import { useEffect, useMemo, useRef, useState } from "react";
import type { LearnosityInitOptions } from "../types";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LearnosityItems: any;
  }
}

interface LearnosityEvents {
  "test:start"?: (...args: unknown[]) => void;
  "test:resume"?: (...args: unknown[]) => void;
  "test:finished:submit"?: (...args: unknown[]) => void;
  [key: string]: ((...args: unknown[]) => void) | undefined;
}

interface UseLearnosityPlayerOptions {
  learnosityInitOptions: LearnosityInitOptions | null;
  sessionId: string | null;
  events: LearnosityEvents;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onReady?: (itemsApi: any) => void;
}

interface UseLearnosityPlayerReturn {
  divId: string;
  loading: boolean;
  error: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsApi: any;
}

const stripNonIdChars = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");

export const useLearnosityPlayer = ({
  learnosityInitOptions,
  sessionId,
  events,
  onReady,
}: UseLearnosityPlayerOptions): UseLearnosityPlayerReturn => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [itemsApi, setItemsApi] = useState<any>(null);
  const [error, setError] = useState(false);
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Ref-based guard to prevent double-init (survives React StrictMode double-fire
  // where state-based checks fail because setState hasn't flushed between fires)
  const initSessionRef = useRef<string | null>(null);

  const divId = useMemo(
    () => `learnosity-${stripNonIdChars(sessionId || "")}-${Date.now()}`,
    [sessionId],
  );

  useEffect(() => {
    console.info("[LearnosityPlayer] useEffect fired", {
      hasInitOptions: !!learnosityInitOptions,
      sessionId,
      divId,
      hasLearnosityItems: !!window.LearnosityItems,
      initSessionRef: initSessionRef.current,
    });

    if (!learnosityInitOptions || !sessionId || !window.LearnosityItems) {
      console.info("[LearnosityPlayer] skipping init — missing deps");
      return;
    }

    // Don't re-init if already initialized for this session
    if (initSessionRef.current === sessionId) {
      console.info(
        "[LearnosityPlayer] skipping init — already initialized for session",
        sessionId,
      );
      return;
    }
    initSessionRef.current = sessionId;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let itemsApiInstance: any = null;

    try {
      console.info("[LearnosityPlayer] calling LearnosityItems.init()", {
        sessionId,
        divId,
        type: (learnosityInitOptions.request as Record<string, unknown>)?.type,
        rendering_type: (
          learnosityInitOptions.request as Record<string, unknown>
        )?.rendering_type,
      });
      itemsApiInstance = window.LearnosityItems.init(
        learnosityInitOptions,
        divId,
        {
          readyListener: () => {
            console.info("[LearnosityPlayer] readyListener fired", {
              sessionId,
              divId,
            });
            onReadyRef.current?.(itemsApiInstance);
          },
          errorListener: (err: unknown) => {
            console.error("[LearnosityPlayer] errorListener fired", {
              sessionId,
              divId,
              err,
            });
            setError(true);
          },
        },
      );

      itemsApiInstance.forSessionId = sessionId;
      setItemsApi(itemsApiInstance);

      // Register event handlers
      Object.keys(eventsRef.current).forEach((key) => {
        const handler = eventsRef.current[key];
        if (handler) {
          itemsApiInstance.on(key, (...args: unknown[]) =>
            handler(itemsApiInstance, ...args),
          );
        }
      });
    } catch (e) {
      console.error("[LearnosityPlayer] Error initializing Learnosity", e);
      setError(true);
    }

    // No cleanup function — intentionally.
    //
    // React StrictMode double-fires effects (mount → cleanup → re-mount).
    // Calling itemsApi.reset() in cleanup destroys Learnosity's DOM state,
    // causing the second init() to fail with "no matching DOM element".
    //
    // Instead, the initSessionRef guard (line 74) blocks redundant inits
    // for the same session. Cleanup happens naturally:
    //   - Real unmount: React removes the DOM container; the JS instance
    //     is garbage-collected with the component closure.
    //   - Session change: new sessionId → new divId → fresh init.
    //     The old instance is orphaned (harmless — its div is gone).
  }, [learnosityInitOptions, sessionId, divId]);

  const loading = !itemsApi && !!sessionId && !!learnosityInitOptions && !error;

  return {
    divId,
    loading,
    error,
    itemsApi,
  };
};
